// src/pages/VenueDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Edit2, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import VenueProfileSetupModal from '../components/modals/VenueProfileSetupModal';
import {
  getVenueProfile,
  saveVenueProfile,
  searchArtistProfiles,
  getArtistProfileForVenue,
  sendHireRequest,
  listenHireRequestsForVenue,
  venueRespondToCounter,
  listenBookingsForVenue,
  signOutUser,
  sendEmailVerification,
  checkEmailVerified,
  getArtistStats,
  rateBookingArtist,
  addPreferredArtist,
  removePreferredArtist,
  getPreferredArtists,
} from '../firebase-utils';

// ── Helper: has booking's event date passed? ──────────────────
const isBookingPast = (booking) => {
  if (!booking.eventDate) return false;
  const eventDT = new Date(`${booking.eventDate}T${booking.eventTime || '23:59'}`);
  return new Date() > eventDT;
};

export default function VenueDashboard({ onModeChange, initialVenueProfile }) {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState('browse');

  // ── Profile state ──
  const [venueProfile, setVenueProfile] = useState(initialVenueProfile || null);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [profileStep, setProfileStep] = useState(1);
  const [emailVerified, setEmailVerified] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileData, setProfileData] = useState(
    initialVenueProfile
      ? { ...initialVenueProfile, socialMedia: initialVenueProfile.socialMedia || { instagram: '', facebook: '', linkedin: '' } }
      : {
          venueName: '', venueType: '', capacity: '', location: '',
          description: '', genres: '', typicalBudget: '',
          phone: '', contactEmail: '', website: '',
          socialMedia: { instagram: '', facebook: '', linkedin: '' },
        }
  );

  // ── Browse state ──
  const [allArtists, setAllArtists] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(false);

  // ── Filter state ──
  const [searchTerm, setSearchTerm] = useState('');
  const [genreFilter, setGenreFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [showFilters, setShowFilters] = useState(false);

  // ── Artist profile modal state ──
  const [viewingArtist, setViewingArtist] = useState(null);
  const [artistStats, setArtistStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // ── Hire form state ──
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [showHireForm, setShowHireForm] = useState(false);
  const [hireFormData, setHireFormData] = useState({
    eventDate: '', eventTime: '', duration: 3, budget: '', description: '',
  });
  const [sendingRequest, setSendingRequest] = useState(false);

  // ── Requests state ──
  const [hireRequests, setHireRequests] = useState([]);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [respondingTo, setRespondingTo] = useState(null);
  const [counterForm, setCounterForm] = useState({ budget: '', message: '' });

  // ── Bookings state ──
  const [bookings, setBookings] = useState([]);

  // ── Rating state ──
  const [ratingBooking, setRatingBooking] = useState(null); // booking being rated
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingReview, setRatingReview] = useState('');
  const [savingRating, setSavingRating] = useState(false);

  // ─── Load venue profile ───────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    checkEmailVerified().then(v => setEmailVerified(v));
    if (initialVenueProfile?.profileComplete) return;
    getVenueProfile(currentUser.uid).then(profile => {
      if (profile?.profileComplete) {
        setVenueProfile(profile);
        setProfileData({
          ...profile,
          socialMedia: profile.socialMedia || { instagram: '', facebook: '', linkedin: '' },
        });
      }
    });
  }, [currentUser]);

  // ─── Listeners ───────────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    const unsub = listenHireRequestsForVenue(currentUser.uid, setHireRequests);
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const unsub = listenBookingsForVenue(currentUser.uid, setBookings);
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    setLoadingArtists(true);
    searchArtistProfiles()
      .then(results => setAllArtists(results))
      .catch(err => console.error('Error loading artists:', err))
      .finally(() => setLoadingArtists(false));
  }, [currentUser]);

  // ── Load preferred artists on mount ──────────────────────
  useEffect(() => {
    if (!currentUser || preferredLoaded) return;
    setLoadingPreferred(true);
    getPreferredArtists(currentUser.uid)
      .then(list => { setPreferredArtists(list); setPreferredLoaded(true); })
      .catch(console.error)
      .finally(() => setLoadingPreferred(false));
  }, [currentUser]);

  // ── Handlers ─────────────────────────────────────────────
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await saveVenueProfile(currentUser.uid, { ...profileData, userId: currentUser.uid, profileComplete: true });
      setVenueProfile({ ...profileData, userId: currentUser.uid });
      setShowProfileSetup(false);
      alert('✅ Venue profile saved!');
    } catch (err) { alert('Error: ' + err.message); }
    finally { setSavingProfile(false); }
  };

  const handleSendVerification = async () => {
    try { await sendEmailVerification(); alert('✅ Verification email sent!'); }
    catch (err) { alert(err.message); }
  };

  const handleCheckVerification = async () => {
    try {
      const verified = await checkEmailVerified();
      setEmailVerified(verified);
      if (verified) alert('✅ Email verified!');
      else alert('❌ Not verified yet.');
    } catch (err) { alert(err.message); }
  };

  // ── Filtered artists ──────────────────────────────────────
  const filteredArtists = (() => {
    let list = [...allArtists];
    const term = searchTerm.toLowerCase().trim();
    if (term) list = list.filter(a =>
      a.artistName.toLowerCase().includes(term) ||
      a.fullName.toLowerCase().includes(term) ||
      a.bio.toLowerCase().includes(term)
    );
    if (genreFilter.trim()) list = list.filter(a => a.genre.toLowerCase().includes(genreFilter.toLowerCase().trim()));
    if (locationFilter.trim()) list = list.filter(a => a.location.toLowerCase().includes(locationFilter.toLowerCase().trim()));
    if (availabilityFilter === 'available') list = list.filter(a => a.isAvailable !== false);
    if (availabilityFilter === 'unavailable') list = list.filter(a => a.isAvailable === false);
    if (minBudget) list = list.filter(a => !a.priceMax || Number(a.priceMax) >= Number(minBudget));
    if (maxBudget) list = list.filter(a => !a.priceMin || Number(a.priceMin) <= Number(maxBudget));
    if (sortBy === 'name') list.sort((a, b) => a.artistName.localeCompare(b.artistName));
    else if (sortBy === 'price_asc') list.sort((a, b) => Number(a.priceMin || 0) - Number(b.priceMin || 0));
    else if (sortBy === 'price_desc') list.sort((a, b) => Number(b.priceMax || 0) - Number(a.priceMax || 0));
    else if (sortBy === 'followers') {
      list.sort((a, b) => {
        const aF = a.followerCounts ? Object.values(a.followerCounts).reduce((s, v) => s + (Number(v) || 0), 0) : 0;
        const bF = b.followerCounts ? Object.values(b.followerCounts).reduce((s, v) => s + (Number(v) || 0), 0) : 0;
        return bF - aF;
      });
    }
    return list;
  })();

  const activeFilterCount = [
    genreFilter, locationFilter,
    availabilityFilter !== 'all' ? availabilityFilter : '',
    minBudget, maxBudget,
    sortBy !== 'name' ? sortBy : '',
  ].filter(Boolean).length;

  const handleViewArtist = async (artist) => {
    setViewingArtist(artist);
    setArtistStats(null);
    setLoadingStats(true);
    try {
      const [freshProfile, stats] = await Promise.all([
        getArtistProfileForVenue(artist.userId || artist.id),
        getArtistStats(artist.userId || artist.id),
      ]);
      if (freshProfile) setViewingArtist(freshProfile);
      setArtistStats(stats);
    } catch (err) { console.error(err); }
    finally { setLoadingStats(false); }
  };

  const handleSendHireRequest = async () => {
    if (!hireFormData.eventDate || !hireFormData.eventTime || !hireFormData.budget) {
      alert('Please fill in date, time, and budget!'); return;
    }
    if (!venueProfile) { alert('Please complete your venue profile first!'); return; }
    setSendingRequest(true);
    try {
      await sendHireRequest({
        venueId: currentUser.uid,
        venueName: venueProfile.venueName,
        venueLocation: venueProfile.location,
        artistId: selectedArtist.userId || selectedArtist.id,
        artistName: selectedArtist.artistName,
        eventDate: hireFormData.eventDate,
        eventTime: hireFormData.eventTime,
        duration: Number(hireFormData.duration),
        budget: Number(hireFormData.budget),
        description: hireFormData.description,
      });
      setShowHireForm(false);
      setSelectedArtist(null);
      setHireFormData({ eventDate: '', eventTime: '', duration: 3, budget: '', description: '' });
      setTab('requests');
      alert('✅ Hire request sent!');
    } catch (err) { alert('Error: ' + err.message); }
    finally { setSendingRequest(false); }
  };

  const handleVenueRespond = async (requestId, action) => {
    try {
      const budget = action === 'counter' ? Number(counterForm.budget) : null;
      if (action === 'counter' && !budget) { alert('Enter a budget amount!'); return; }
      await venueRespondToCounter(requestId, action, budget, counterForm.message);
      setRespondingTo(null);
      setCounterForm({ budget: '', message: '' });
      alert(action === 'accept' ? '✅ Accepted! Booking confirmed!' : action === 'counter' ? '✅ Counter sent!' : '✅ Declined.');
    } catch (err) { alert('Error: ' + err.message); }
  };

  // ── Book Again: pre-fill hire form with same artist ───────
  const handleBookAgain = (booking) => {
    // Find the artist in allArtists, or construct a minimal object
    const artist = allArtists.find(a => (a.userId || a.id) === booking.artistId)
      || { userId: booking.artistId, id: booking.artistId, artistName: booking.artistName };
    setSelectedArtist(artist);
    setHireFormData({
      eventDate: '',
      eventTime: booking.eventTime || '',
      duration: booking.duration || 3,
      budget: booking.agreedBudget ? String(booking.agreedBudget) : '',
      description: '',
    });
    setShowHireForm(true);
  };

  // ── Submit rating ─────────────────────────────────────────
  const handleSubmitRating = async () => {
    if (ratingValue === 0) { alert('Please select a star rating!'); return; }
    setSavingRating(true);
    try {
      await rateBookingArtist(
        ratingBooking.id,
        currentUser.uid,
        ratingBooking.artistId,
        ratingValue,
        ratingReview
      );
      // Update local bookings state so the UI reflects immediately
      setBookings(prev => prev.map(b =>
        b.id === ratingBooking.id
          ? { ...b, venueRating: ratingValue, venueReview: ratingReview }
          : b
      ));
      setRatingBooking(null);
      setRatingValue(0);
      setRatingReview('');
      alert('✅ Rating submitted! Thank you for the feedback.');
    } catch (err) { alert('Error: ' + err.message); }
    finally { setSavingRating(false); }
  };

  // ── Calendar state ──
  const [calendarDate, setCalendarDate] = useState(new Date());

  // ── Preferred artists state ──
  const [preferredArtists, setPreferredArtists] = useState([]);
  const [loadingPreferred, setLoadingPreferred] = useState(false);
  const [preferredLoaded, setPreferredLoaded] = useState(false);

  // ── Preferred artist handlers ────────────────────────────
  const isPreferred = (artistId) => preferredArtists.some(p => p.id === artistId || p.artistId === artistId);

  const handleTogglePreferred = async (artist) => {
    const artistId = artist.userId || artist.id;
    if (isPreferred(artistId)) {
      try {
        await removePreferredArtist(currentUser.uid, artistId);
        setPreferredArtists(prev => prev.filter(p => p.id !== artistId && p.artistId !== artistId));
      } catch (err) { alert('Error: ' + err.message); }
    } else {
      try {
        await addPreferredArtist(currentUser.uid, artist);
        setPreferredArtists(prev => [...prev, {
          id: artistId, artistId,
          artistName: artist.artistName || '',
          genre: artist.genre || '',
          location: artist.location || '',
          priceMin: artist.priceMin || '',
          priceMax: artist.priceMax || '',
          isAvailable: artist.isAvailable !== false,
        }]);
      } catch (err) { alert('Error: ' + err.message); }
    }
  };

  const statusColor = { pending: '#3b82f6', countered: '#f59e0b', accepted: '#22c55e', declined: '#ef4444' };
  const statusLabel = { pending: '⏳ Pending', countered: '🔄 Countered', accepted: '✅ Accepted', declined: '❌ Declined' };
  const actionableCount = hireRequests.filter(r => r.status === 'countered').length;
  const pastBookings = bookings.filter(isBookingPast);
  const unratedPastCount = pastBookings.filter(b => !b.venueRating).length;

  return (
    <div className="artist-dashboard rock-background min-h-screen p-4 pb-24 md:pb-4">

      <VenueProfileSetupModal
        show={showProfileSetup}
        currentUser={currentUser}
        emailVerified={emailVerified}
        profileStep={profileStep}
        setProfileStep={setProfileStep}
        profileData={profileData}
        setProfileData={setProfileData}
        handleSaveProfile={handleSaveProfile}
        handleSendVerification={handleSendVerification}
        handleCheckVerification={handleCheckVerification}
        saving={savingProfile}
      />

      <div className="max-w-6xl mx-auto pt-6">

        {/* Header */}
        <div className="artist-header-card gig-card mb-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h1 className="artist-dashboard-title mb-2">🏢 VENUE DASHBOARD</h1>
              <p className="artist-welcome">{venueProfile?.venueName || currentUser?.email}</p>
              {venueProfile?.location && <p className="text-gray-400 text-sm">📍 {venueProfile.location}</p>}
              {venueProfile?.venueType && <p className="text-gray-400 text-sm">🏛️ {venueProfile.venueType} · {venueProfile.capacity} capacity</p>}
              {/* Public profile shareable link */}
              {venueProfile?.venueName && (() => {
                const slug = (venueProfile.slug || venueProfile.venueName.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 60));
                const url = `https://gigwave.in/venue/${slug}`;
                return (
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="text-xs text-gray-500">🔗 Public page:</span>
                    <a href={url} target="_blank" rel="noopener noreferrer"
                      className="text-xs font-bold hover:underline" style={{ color: '#00d4ff' }}>{url}</a>
                    <button
                      onClick={() => navigator.clipboard.writeText(url).then(() => alert('✅ Link copied!')).catch(() => prompt('Copy link:', url))}
                      className="text-xs px-2 py-0.5 rounded font-bold"
                      style={{ background: 'rgba(0,212,255,0.12)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.3)' }}>
                      📋 Copy
                    </button>
                  </div>
                );
              })()}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => setShowProfileSetup(true)} className="btn btn-electric">
                <Edit2 size={18} /><span>Edit Profile</span>
              </button>
              <button onClick={() => onModeChange('discover')} className="btn btn-ghost">← Back</button>
              <button onClick={async () => { await signOutUser(); onModeChange('discover'); }} className="btn btn-fire">Sign Out</button>
            </div>
          </div>
        </div>

        {/* Unrated bookings prompt */}
        {unratedPastCount > 0 && (
          <div className="mb-6 p-4 rounded-xl flex flex-col md:flex-row md:items-center gap-3"
            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)' }}>
            <div className="flex-1">
              <p className="text-yellow-400 font-bold">⭐ Rate your recent artists</p>
              <p className="text-gray-400 text-sm mt-0.5">
                You have {unratedPastCount} past {unratedPastCount === 1 ? 'booking' : 'bookings'} without a rating.
                Your feedback helps artists improve and makes future hiring easier.
              </p>
            </div>
            <button
              onClick={() => setTab('bookings')}
              className="btn btn-ghost text-sm whitespace-nowrap"
              style={{ borderColor: 'rgba(245,158,11,0.5)', color: '#fbbf24' }}>
              Go to Bookings →
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="artist-nav-tabs">
          {[
            { key: 'browse', label: '🔍 Browse Artists' },
            { key: 'requests', label: `📨 My Requests${actionableCount > 0 ? ` (${actionableCount})` : ''}` },
            { key: 'bookings', label: `📅 Bookings${bookings.length > 0 ? ` (${bookings.length})` : ''}${unratedPastCount > 0 ? ` · ${unratedPastCount} to rate` : ''}` },
            { key: 'calendar', label: '🗓️ Calendar' },
            { key: 'preferred', label: `⭐ Preferred${preferredArtists.length > 0 ? ` (${preferredArtists.length})` : ''}` },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`artist-nav-tab ${tab === key ? 'active' : ''}`}>
              {label}
            </button>
          ))}
        </div>

        {/* ══ BROWSE ARTISTS ══ */}
        {tab === 'browse' && (
          <div className="space-y-4">
            <div className="artist-search-section">
              <h3 className="artist-section-header">
                <span className="emoji-icon">🎸</span><span>FIND ARTISTS</span>
                {allArtists.length > 0 && (
                  <span className="ml-auto text-sm font-normal text-gray-400">
                    {filteredArtists.length} of {allArtists.length} artists
                  </span>
                )}
              </h3>
              <div className="flex gap-3 mb-3">
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search by name or bio..." className="artist-search-input flex-1" />
                <button onClick={() => setShowFilters(f => !f)}
                  className={`btn text-sm whitespace-nowrap ${showFilters ? 'btn-electric' : 'btn-ghost'}`}>
                  🎛️ Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </button>
                {(searchTerm || activeFilterCount > 0) && (
                  <button onClick={() => { setSearchTerm(''); setGenreFilter(''); setLocationFilter(''); setAvailabilityFilter('all'); setMinBudget(''); setMaxBudget(''); setSortBy('name'); }}
                    className="btn btn-ghost text-sm whitespace-nowrap">✕ Clear</button>
                )}
              </div>

              {showFilters && (
                <div className="rounded-xl p-4 mb-4 space-y-4"
                  style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-electric text-xs font-bold uppercase tracking-wider block mb-1">🎸 Genre</label>
                      <input type="text" value={genreFilter} onChange={e => setGenreFilter(e.target.value)} placeholder="e.g. Rock, Jazz, Pop..." className="artist-search-input w-full" />
                    </div>
                    <div>
                      <label className="text-electric text-xs font-bold uppercase tracking-wider block mb-1">📍 Location</label>
                      <input type="text" value={locationFilter} onChange={e => setLocationFilter(e.target.value)} placeholder="e.g. Mumbai, Pune..." className="artist-search-input w-full" />
                    </div>
                    <div>
                      <label className="text-electric text-xs font-bold uppercase tracking-wider block mb-1">💰 Your Budget Range (₹)</label>
                      <div className="flex gap-2 items-center">
                        <input type="number" value={minBudget} onChange={e => setMinBudget(e.target.value)} placeholder="Min" className="artist-search-input w-full" />
                        <span className="text-gray-400 text-sm flex-shrink-0">to</span>
                        <input type="number" value={maxBudget} onChange={e => setMaxBudget(e.target.value)} placeholder="Max" className="artist-search-input w-full" />
                      </div>
                    </div>
                    <div>
                      <label className="text-electric text-xs font-bold uppercase tracking-wider block mb-1">↕️ Sort By</label>
                      <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="artist-search-input w-full" style={{ background: 'rgba(0,0,0,0.6)' }}>
                        <option value="name">Name (A–Z)</option>
                        <option value="price_asc">Price: Low to High</option>
                        <option value="price_desc">Price: High to Low</option>
                        <option value="followers">Most Followers</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-electric text-xs font-bold uppercase tracking-wider block mb-2">✅ Availability</label>
                    <div className="flex gap-2">
                      {[{ val: 'all', label: 'All Artists' }, { val: 'available', label: '✅ Available Only' }, { val: 'unavailable', label: '❌ Not Available' }].map(({ val, label }) => (
                        <button key={val} onClick={() => setAvailabilityFilter(val)}
                          className="text-sm px-3 py-1.5 rounded-lg font-bold transition-all"
                          style={{ background: availabilityFilter === val ? 'rgba(0,212,255,0.25)' : 'rgba(255,255,255,0.07)', color: availabilityFilter === val ? '#00d4ff' : '#9ca3af', border: availabilityFilter === val ? '1px solid rgba(0,212,255,0.5)' : '1px solid rgba(255,255,255,0.1)' }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {loadingArtists && (
                <div className="text-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-electric mx-auto mb-3"></div>
                  <p className="text-electric font-bold">Loading artists...</p>
                </div>
              )}
              {!loadingArtists && allArtists.length === 0 && <div className="artist-empty-state"><p className="artist-empty-text">No artists have signed up yet. Check back soon! 🎵</p></div>}
              {!loadingArtists && allArtists.length > 0 && filteredArtists.length === 0 && <div className="artist-empty-state"><p className="artist-empty-text">No artists match your filters. Try adjusting them! 🔍</p></div>}

              {!loadingArtists && (
                <div className="space-y-4">
                  {filteredArtists.map(artist => (
                    <div key={artist.id} className="artist-song-card p-6">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="text-xl font-bold text-white">{artist.artistName}</h3>
                            {artist.isAvailable === false ? (
                              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)' }}>❌ Not Available</span>
                            ) : (
                              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)' }}>✅ Available</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3 mb-2">
                            {artist.genre && <p className="text-electric text-sm font-semibold">🎸 {artist.genre}</p>}
                            {artist.location && <p className="text-gray-400 text-sm">📍 {artist.location}</p>}
                          </div>
                          {(artist.priceMin || artist.priceMax) && (
                            <p className="text-neon text-sm font-bold mb-2">
                              💰 ₹{artist.priceMin ? Number(artist.priceMin).toLocaleString() : '?'}
                              {artist.priceMax ? ` – ₹${Number(artist.priceMax).toLocaleString()}` : '+'}
                              <span className="text-gray-400 font-normal"> per event</span>
                            </p>
                          )}
                          {artist.availabilityNote && <p className="text-yellow-400 text-xs mb-2">📅 {artist.availabilityNote}</p>}
                          {artist.bio && <p className="text-gray-300 text-sm mt-1 line-clamp-2">{artist.bio}</p>}
                          {!artist.followerCountsBlocked && artist.followerCounts && Object.values(artist.followerCounts).some(v => v) && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {[{ key: 'instagram', icon: '📸', color: '#E1306C' }, { key: 'youtube', icon: '▶️', color: '#FF0000' }, { key: 'spotify', icon: '🎵', color: '#1DB954' }, { key: 'tiktok', icon: '🎵', color: '#ee1d52' }, { key: 'facebook', icon: '👍', color: '#1877F2' }].map(({ key, icon, color }) => {
                                const val = artist.followerCounts[key];
                                if (!val) return null;
                                return <span key={key} className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${color}15`, color, border: `1px solid ${color}35` }}>{icon} {Number(val).toLocaleString()}</span>;
                              })}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 self-start flex-shrink-0">
                          <button onClick={() => handleViewArtist(artist)} className="btn btn-ghost text-sm whitespace-nowrap">👤 View Profile</button>
                          <button onClick={() => { setSelectedArtist(artist); setShowHireForm(true); }} className="btn btn-electric whitespace-nowrap">
                            <Send size={16} /><span>Hire Artist</span>
                          </button>
                          <button
                            onClick={() => handleTogglePreferred(artist)}
                            className="btn text-sm whitespace-nowrap"
                            style={{
                              background: isPreferred(artist.userId || artist.id) ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.06)',
                              color: isPreferred(artist.userId || artist.id) ? '#fbbf24' : '#6b7280',
                              border: isPreferred(artist.userId || artist.id) ? '1px solid rgba(245,158,11,0.45)' : '1px solid rgba(255,255,255,0.1)',
                            }}>
                            {isPreferred(artist.userId || artist.id) ? '⭐ Preferred' : '☆ Add to Preferred'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ MY REQUESTS ══ */}
        {tab === 'requests' && (
          <div className="space-y-4">
            <div className="artist-section-card">
              <h2 className="artist-section-header"><span className="emoji-icon">📨</span><span>HIRE REQUESTS</span></h2>
              {hireRequests.length === 0 ? (
                <div className="artist-empty-state"><p className="artist-empty-text">No hire requests yet. Browse artists to get started! 🎸</p></div>
              ) : (
                <div className="space-y-4">
                  {hireRequests.map(req => (
                    <div key={req.id} className="artist-song-card p-6">
                      <div className="flex flex-col md:flex-row md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-white">{req.artistName}</h3>
                            <span className="text-xs font-bold px-2 py-1 rounded-full"
                              style={{ background: `${statusColor[req.status]}22`, color: statusColor[req.status], border: `1px solid ${statusColor[req.status]}55` }}>
                              {statusLabel[req.status]}
                            </span>
                          </div>
                          <p className="text-electric font-bold">📅 {req.eventDate} at {req.eventTime}</p>
                          <p className="text-gray-300 text-sm">⏱️ {req.duration}h &nbsp;·&nbsp; 💰 Current: ₹{req.currentBudget?.toLocaleString()}</p>
                          {req.description && <p className="text-gray-400 text-sm mt-1">{req.description}</p>}
                        </div>
                        <button onClick={() => setExpandedRequest(expandedRequest === req.id ? null : req.id)}
                          className="btn btn-ghost text-sm self-start">
                          {expandedRequest === req.id ? '▲ Hide' : '▼ History'}
                        </button>
                      </div>
                      {expandedRequest === req.id && (
                        <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
                          <p className="text-white font-bold text-sm mb-3">🕐 Negotiation History</p>
                          {(req.negotiationHistory || []).map((entry, i) => (
                            <div key={i} className={`p-3 rounded-lg text-sm ${entry.by === 'venue' ? 'mr-8' : 'ml-8'}`}
                              style={{ background: entry.by === 'venue' ? 'rgba(59,130,246,0.12)' : 'rgba(168,85,247,0.12)', border: `1px solid ${entry.by === 'venue' ? 'rgba(59,130,246,0.3)' : 'rgba(168,85,247,0.3)'}` }}>
                              <div className="flex justify-between mb-1">
                                <span className="font-bold" style={{ color: entry.by === 'venue' ? '#60a5fa' : '#c084fc' }}>
                                  {entry.by === 'venue' ? '🏢 You' : '🎸 Artist'} · {entry.action}
                                </span>
                                <span className="text-gray-400 text-xs">{entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : ''}</span>
                              </div>
                              <p className="text-white font-semibold">💰 ₹{entry.budget?.toLocaleString()}</p>
                              {entry.message && <p className="text-gray-300 mt-1">{entry.message}</p>}
                            </div>
                          ))}
                          {req.status === 'countered' && (
                            <div className="mt-3 p-4 rounded-xl" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)' }}>
                              <p className="text-yellow-400 font-bold text-sm mb-3">🔄 Artist countered — Your move:</p>
                              {respondingTo === req.id ? (
                                <div className="space-y-3">
                                  <input type="number" value={counterForm.budget} onChange={e => setCounterForm({ ...counterForm, budget: e.target.value })} placeholder="Your counter budget (₹)" className="w-full px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:border-electric focus:outline-none text-sm" />
                                  <textarea value={counterForm.message} onChange={e => setCounterForm({ ...counterForm, message: e.target.value })} placeholder="Message to artist (optional)" rows={2} className="w-full px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:border-electric focus:outline-none text-sm resize-none" />
                                  <div className="flex flex-wrap gap-2">
                                    <button onClick={() => handleVenueRespond(req.id, 'accept')} className="btn btn-electric text-sm">✅ Accept</button>
                                    <button onClick={() => handleVenueRespond(req.id, 'counter')} className="btn btn-neon text-sm">🔄 Counter</button>
                                    <button onClick={() => handleVenueRespond(req.id, 'decline')} className="btn btn-fire text-sm">❌ Decline</button>
                                    <button onClick={() => setRespondingTo(null)} className="btn btn-ghost text-sm">Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <button onClick={() => setRespondingTo(req.id)} className="btn btn-electric text-sm">💬 Respond to Counter</button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ BOOKINGS ══ */}
        {tab === 'bookings' && (
          <div className="space-y-4">
            <div className="artist-section-card">
              <h2 className="artist-section-header"><span className="emoji-icon">📅</span><span>CONFIRMED BOOKINGS</span></h2>
              {bookings.length === 0 ? (
                <div className="artist-empty-state"><p className="artist-empty-text">No confirmed bookings yet. Send hire requests to artists! 🎸</p></div>
              ) : (
                <div className="space-y-4">
                  {/* Sort: upcoming first, then past */}
                  {[...bookings].sort((a, b) => {
                    const aDate = new Date(`${a.eventDate}T${a.eventTime || '00:00'}`);
                    const bDate = new Date(`${b.eventDate}T${b.eventTime || '00:00'}`);
                    const aPast = isBookingPast(a);
                    const bPast = isBookingPast(b);
                    if (aPast !== bPast) return aPast ? 1 : -1; // upcoming first
                    return aPast ? bDate - aDate : aDate - bDate; // upcoming: soonest first; past: newest first
                  }).map(booking => {
                    const isPast = isBookingPast(booking);
                    const hasRated = !!booking.venueRating;

                    return (
                      <div key={booking.id} className="artist-song-card p-6"
                        style={isPast && !hasRated ? { borderColor: 'rgba(245,158,11,0.4)' } : {}}>
                        <div className="flex flex-col md:flex-row md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="text-xl font-bold text-white">🎸 {booking.artistName}</h3>
                              {isPast ? (
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                                  style={{ background: 'rgba(107,114,128,0.2)', color: '#9ca3af', border: '1px solid rgba(107,114,128,0.4)' }}>
                                  ⚫ Past
                                </span>
                              ) : (
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                                  style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)' }}>
                                  ✅ Upcoming
                                </span>
                              )}
                            </div>
                            <p className="text-electric font-bold">📅 {booking.eventDate} at {booking.eventTime}</p>
                            <p className="text-gray-300 text-sm">⏱️ {booking.duration} hours &nbsp;·&nbsp; 💰 ₹{booking.agreedBudget?.toLocaleString()} agreed</p>

                            {/* Existing rating display */}
                            {hasRated && (
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-yellow-400 text-sm">
                                  {'⭐'.repeat(booking.venueRating)}{'☆'.repeat(5 - booking.venueRating)}
                                </span>
                                <span className="text-gray-400 text-xs">Your rating</span>
                                {booking.venueReview && (
                                  <span className="text-gray-400 text-xs italic">· "{booking.venueReview}"</span>
                                )}
                              </div>
                            )}

                            {/* Unrated past booking prompt */}
                            {isPast && !hasRated && (
                              <p className="text-yellow-400 text-xs mt-2 font-semibold">
                                ⭐ How was this performance? Rate the artist!
                              </p>
                            )}
                          </div>

                          {/* Action buttons */}
                          <div className="flex flex-col gap-2 self-start flex-shrink-0">
                            {/* Rate / Update Rating */}
                            {isPast && (
                              <button
                                onClick={() => {
                                  setRatingBooking(booking);
                                  setRatingValue(booking.venueRating || 0);
                                  setRatingReview(booking.venueReview || '');
                                }}
                                className="btn text-sm whitespace-nowrap"
                                style={{
                                  background: hasRated ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.2)',
                                  color: '#fbbf24',
                                  border: '1px solid rgba(245,158,11,0.45)',
                                }}>
                                {hasRated ? '✏️ Update Rating' : '⭐ Rate Artist'}
                              </button>
                            )}
                            {/* Book Again */}
                            <button
                              onClick={() => handleBookAgain(booking)}
                              className="btn btn-electric text-sm whitespace-nowrap">
                              🔁 Book Again
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {/* ══ PREFERRED ARTISTS TAB ══ */}
        {tab === 'preferred' && (
          <div className="space-y-4">
            <div className="artist-section-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="artist-section-header mb-0"><span className="emoji-icon">⭐</span><span>PREFERRED ARTISTS</span></h2>
                <p className="text-gray-500 text-xs">Your shortlist for future bookings</p>
              </div>

              {loadingPreferred && (
                <div className="text-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-electric mx-auto mb-3"></div>
                  <p className="text-electric font-bold">Loading...</p>
                </div>
              )}

              {!loadingPreferred && preferredArtists.length === 0 && (
                <div className="artist-empty-state">
                  <p className="text-4xl mb-3">⭐</p>
                  <p className="artist-empty-text">No preferred artists yet.</p>
                  <p className="text-gray-500 text-sm mt-2">Browse artists and click "☆ Add to Preferred" to build your shortlist.</p>
                </div>
              )}

              {!loadingPreferred && preferredArtists.length > 0 && (
                <div className="space-y-4">
                  {preferredArtists.map(artist => {
                    const artistId = artist.artistId || artist.id;
                    const fullArtist = allArtists.find(a => (a.userId || a.id) === artistId);
                    return (
                      <div key={artistId} className="artist-song-card p-5">
                        <div className="flex flex-col md:flex-row md:items-start gap-4">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="text-xl font-bold text-white">{artist.artistName}</h3>
                              {artist.isAvailable === false ? (
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                                  style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)' }}>❌ Not Available</span>
                              ) : (
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                                  style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)' }}>✅ Available</span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-3 mb-1">
                              {artist.genre && <p className="text-electric text-sm font-semibold">🎸 {artist.genre}</p>}
                              {artist.location && <p className="text-gray-400 text-sm">📍 {artist.location}</p>}
                            </div>
                            {(artist.priceMin || artist.priceMax) && (
                              <p className="text-neon text-sm font-bold">
                                💰 ₹{artist.priceMin ? Number(artist.priceMin).toLocaleString() : '?'}
                                {artist.priceMax ? ` – ₹${Number(artist.priceMax).toLocaleString()}` : '+'}
                                <span className="text-gray-400 font-normal"> per event</span>
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col gap-2 self-start flex-shrink-0">
                            <button
                              onClick={() => {
                                const target = fullArtist || artist;
                                setSelectedArtist({ ...target, userId: artistId });
                                setShowHireForm(true);
                              }}
                              className="btn btn-electric text-sm whitespace-nowrap">
                              <Send size={14} /><span>Hire</span>
                            </button>
                            {fullArtist && (
                              <button
                                onClick={() => handleViewArtist(fullArtist)}
                                className="btn btn-ghost text-sm whitespace-nowrap">
                                👤 Profile
                              </button>
                            )}
                            <button
                              onClick={() => handleTogglePreferred(artist)}
                              className="btn text-sm whitespace-nowrap"
                              style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}>
                              ✕ Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ CALENDAR TAB ══ */}
        {tab === 'calendar' && (() => {
          const year = calendarDate.getFullYear();
          const month = calendarDate.getMonth();

          // Build a set of booking dates for quick lookup
          const bookingsByDate = {};
          bookings.forEach(b => {
            if (!b.eventDate) return;
            if (!bookingsByDate[b.eventDate]) bookingsByDate[b.eventDate] = [];
            bookingsByDate[b.eventDate].push(b);
          });

          // Calendar grid
          const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const today = new Date();
          const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

          const cells = [];
          for (let i = 0; i < firstDay; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) cells.push(d);

          const monthLabel = calendarDate.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

          return (
            <div className="space-y-4">
              <div className="artist-section-card">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="artist-section-header mb-0"><span className="emoji-icon">🗓️</span><span>BOOKING CALENDAR</span></h2>
                </div>

                {/* Month navigation */}
                <div className="flex items-center justify-between mb-5">
                  <button
                    onClick={() => setCalendarDate(new Date(year, month - 1, 1))}
                    className="btn btn-ghost text-sm px-3 py-2">← Prev</button>
                  <h3 className="text-white font-bold text-lg">{monthLabel}</h3>
                  <button
                    onClick={() => setCalendarDate(new Date(year, month + 1, 1))}
                    className="btn btn-ghost text-sm px-3 py-2">Next →</button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 mb-2">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                    <div key={d} className="text-center text-xs font-bold text-gray-500 py-1">{d}</div>
                  ))}
                </div>

                {/* Day cells */}
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((day, idx) => {
                    if (!day) return <div key={`empty-${idx}`} />;
                    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const dayBookings = bookingsByDate[dateStr] || [];
                    const hasBooking = dayBookings.length > 0;
                    const isToday = dateStr === todayStr;
                    const isPast = dateStr < todayStr;

                    return (
                      <div key={dateStr}
                        className="rounded-lg p-1 min-h-[52px] flex flex-col items-center cursor-default transition-all"
                        style={{
                          background: hasBooking
                            ? 'rgba(0,212,255,0.12)'
                            : isToday
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(255,255,255,0.02)',
                          border: isToday
                            ? '1px solid rgba(0,212,255,0.6)'
                            : hasBooking
                            ? '1px solid rgba(0,212,255,0.3)'
                            : '1px solid rgba(255,255,255,0.06)',
                        }}>
                        <span className={`text-xs font-bold mt-0.5 ${
                          isToday ? 'text-electric' :
                          isPast ? 'text-gray-600' :
                          'text-white'
                        }`}>{day}</span>
                        {dayBookings.map((b, i) => (
                          <div key={i}
                            className="w-full mt-0.5 px-1 py-0.5 rounded text-center truncate"
                            style={{ background: 'rgba(0,212,255,0.2)', fontSize: '9px', color: '#00d4ff', fontWeight: 700, maxWidth: '100%' }}
                            title={`${b.artistName} · ₹${b.agreedBudget?.toLocaleString()}`}>
                            {b.artistName?.split(' ')[0]}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded" style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)' }}></div>
                    <span className="text-xs text-gray-400">Has booking</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(0,212,255,0.6)' }}></div>
                    <span className="text-xs text-gray-400">Today</span>
                  </div>
                </div>

                {/* Bookings this month */}
                {(() => {
                  const thisMonthStr = `${year}-${String(month+1).padStart(2,'0')}`;
                  const thisMonthBookings = bookings
                    .filter(b => b.eventDate?.startsWith(thisMonthStr))
                    .sort((a, b) => a.eventDate.localeCompare(b.eventDate));
                  if (thisMonthBookings.length === 0) return (
                    <div className="mt-6 text-center py-6 rounded-xl"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)' }}>
                      <p className="text-gray-500 text-sm">No bookings in {monthLabel}</p>
                    </div>
                  );
                  return (
                    <div className="mt-6">
                      <p className="text-electric text-xs font-bold uppercase tracking-wider mb-3">
                        {thisMonthBookings.length} Booking{thisMonthBookings.length !== 1 ? 's' : ''} in {monthLabel}
                      </p>
                      <div className="space-y-2">
                        {thisMonthBookings.map(b => {
                          const bPast = isBookingPast(b);
                          return (
                            <div key={b.id}
                              className="flex items-center justify-between p-3 rounded-xl gap-3"
                              style={{
                                background: bPast ? 'rgba(255,255,255,0.03)' : 'rgba(0,212,255,0.06)',
                                border: bPast ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,212,255,0.2)',
                              }}>
                              <div className="flex-1 min-w-0">
                                <p className="text-white font-bold text-sm truncate">🎸 {b.artistName}</p>
                                <p className="text-gray-400 text-xs">
                                  📅 {b.eventDate}{b.eventTime ? ` at ${b.eventTime}` : ''}
                                  {b.duration ? ` · ⏱️ ${b.duration}h` : ''}
                                </p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-green-400 font-bold text-sm">₹{b.agreedBudget?.toLocaleString()}</p>
                                {bPast ? (
                                  <span className="text-xs text-gray-500">Past</span>
                                ) : (
                                  <span className="text-xs font-bold" style={{ color: '#22c55e' }}>Upcoming</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })()}

      </div>

      {/* ── Rating Modal ── */}
      {ratingBooking && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="gig-card w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <h2 className="text-xl font-bold text-white">⭐ Rate {ratingBooking.artistName}</h2>
                <p className="text-gray-400 text-sm mt-0.5">
                  {ratingBooking.eventDate} · ₹{ratingBooking.agreedBudget?.toLocaleString()}
                </p>
              </div>
              <button onClick={() => { setRatingBooking(null); setRatingValue(0); setRatingReview(''); }}
                className="text-white hover:text-neon text-2xl leading-none">✕</button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Stars */}
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-3">How was the performance?</p>
                <div className="flex justify-center gap-3 mb-2">
                  {[1,2,3,4,5].map(star => (
                    <button key={star} onClick={() => setRatingValue(star)}
                      className="transition-transform hover:scale-110"
                      style={{ fontSize: '2.5rem', lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}>
                      {star <= ratingValue ? '⭐' : '☆'}
                    </button>
                  ))}
                </div>
                {ratingValue > 0 && (
                  <p className="text-electric font-bold text-sm">
                    {['','Poor','Fair','Good','Great','Excellent!'][ratingValue]}
                  </p>
                )}
              </div>

              {/* Written review */}
              <div>
                <label className="text-white text-sm font-bold block mb-2">
                  Write a review <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <textarea
                  value={ratingReview}
                  onChange={e => setRatingReview(e.target.value)}
                  placeholder="Great energy, crowd loved it! Would definitely book again..."
                  maxLength={300}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg text-white text-sm resize-none focus:outline-none"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)' }} />
                <p className="text-right text-gray-500 text-xs mt-1">{ratingReview.length}/300</p>
              </div>

              {/* Would hire again */}
              <div className="p-3 rounded-lg text-sm"
                style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)' }}>
                <p className="text-electric text-xs font-bold mb-1">💡 Tip</p>
                <p className="text-gray-400 text-xs">
                  Use "🔁 Book Again" button anytime to re-hire this artist with the same terms pre-filled.
                </p>
              </div>
            </div>

            <div className="flex gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={handleSubmitRating}
                disabled={ratingValue === 0 || savingRating}
                className="flex-1 btn btn-electric"
                style={{ opacity: ratingValue === 0 ? 0.5 : 1 }}>
                {savingRating ? '⏳ Saving...' : ratingBooking.venueRating ? '✏️ Update Rating' : '✅ Submit Rating'}
              </button>
              <button onClick={() => { setRatingBooking(null); setRatingValue(0); setRatingReview(''); }}
                className="btn btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Artist Profile Modal ── */}
      {viewingArtist && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="gig-card w-full max-w-2xl my-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white">🎸 Artist Profile</h2>
              <button onClick={() => { setViewingArtist(null); setArtistStats(null); }} className="text-white hover:text-neon text-2xl">✕</button>
            </div>
            <div className="px-6 py-5 space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-3xl font-bold text-white mb-1">{viewingArtist.artistName}</h3>
                  {viewingArtist.fullName && <p className="text-gray-400">{viewingArtist.fullName}</p>}
                  {viewingArtist.gigwaveFollowerCount > 0 && (
                    <p className="text-sm font-bold mt-1" style={{ color: '#c084fc' }}>
                      👥 {Number(viewingArtist.gigwaveFollowerCount).toLocaleString()} GigWave {viewingArtist.gigwaveFollowerCount === 1 ? 'follower' : 'followers'}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {viewingArtist.genre && (
                      <span className="px-3 py-1 rounded-full text-sm font-bold" style={{ background: 'rgba(0,212,255,0.15)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.3)' }}>🎸 {viewingArtist.genre}</span>
                    )}
                    {viewingArtist.location && (
                      <span className="px-3 py-1 rounded-full text-sm" style={{ background: 'rgba(255,255,255,0.08)', color: '#9ca3af' }}>📍 {viewingArtist.location}</span>
                    )}
                    {viewingArtist.isAvailable === false ? (
                      <span className="px-3 py-1 rounded-full text-sm font-bold" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)' }}>❌ Not Available</span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-sm font-bold" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)' }}>✅ Available for Hire</span>
                    )}
                  </div>
                </div>
                {(viewingArtist.priceMin || viewingArtist.priceMax) && (
                  <div className="text-right">
                    <p className="text-gray-400 text-xs mb-1">Price Range</p>
                    <p className="text-2xl font-bold text-neon">
                      ₹{viewingArtist.priceMin ? Number(viewingArtist.priceMin).toLocaleString() : '?'}
                      {viewingArtist.priceMax ? ` – ₹${Number(viewingArtist.priceMax).toLocaleString()}` : '+'}
                    </p>
                    <p className="text-gray-400 text-xs">per event</p>
                  </div>
                )}
              </div>
              {viewingArtist.availabilityNote && (
                <div className="p-3 rounded-lg" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)' }}>
                  <p className="text-yellow-400 text-sm">📅 {viewingArtist.availabilityNote}</p>
                </div>
              )}
              {viewingArtist.bio && (
                <div>
                  <p className="text-electric text-xs font-bold uppercase tracking-wider mb-2">About</p>
                  <p className="text-gray-300 leading-relaxed">{viewingArtist.bio}</p>
                </div>
              )}
              <div>
                <p className="text-electric text-xs font-bold uppercase tracking-wider mb-3">Performance Stats</p>
                {loadingStats ? (
                  <div className="flex gap-3">{[1,2,3,4].map(i => <div key={i} className="flex-1 h-16 rounded-lg animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />)}</div>
                ) : artistStats ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Total Gigs', value: artistStats.totalGigs, icon: '🎸', color: '#00d4ff' },
                      { label: 'Avg Rating', value: artistStats.averageRating ? `${artistStats.averageRating}★` : 'N/A', icon: '⭐', color: '#f59e0b', sub: artistStats.ratingCount > 0 ? `${artistStats.ratingCount} reviews` : null },
                      { label: 'Total Audience', value: artistStats.totalAudience, icon: '👥', color: '#a855f7' },
                      { label: 'Song Votes', value: artistStats.totalVotes, icon: '⚡', color: '#22c55e' },
                    ].map(({ label, value, icon, color, sub }) => (
                      <div key={label} className="p-4 rounded-xl text-center" style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
                        <div className="text-2xl mb-1">{icon}</div>
                        <div className="text-2xl font-bold text-white">{value}</div>
                        <div className="text-xs text-gray-400 mt-1">{label}</div>
                        {sub && <div className="text-xs mt-0.5" style={{ color }}>{sub}</div>}
                      </div>
                    ))}
                  </div>
                ) : <p className="text-gray-500 text-sm">Could not load stats.</p>}
              </div>
              {artistStats?.upcomingGigs > 0 && (
                <div className="p-3 rounded-lg" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)' }}>
                  <p className="text-blue-400 text-sm">📅 {artistStats.upcomingGigs} upcoming gig{artistStats.upcomingGigs > 1 ? 's' : ''} scheduled</p>
                </div>
              )}
              {viewingArtist.socialMedia && Object.values(viewingArtist.socialMedia).some(v => v) && (
                <div>
                  <p className="text-electric text-xs font-bold uppercase tracking-wider mb-3">Social Media</p>
                  <div className="flex flex-wrap gap-2">
                    {[{ key: 'instagram', label: '📸 Instagram', color: '#E1306C' }, { key: 'youtube', label: '▶️ YouTube', color: '#FF0000' }, { key: 'spotify', label: '🎵 Spotify', color: '#1DB954' }, { key: 'facebook', label: '👍 Facebook', color: '#1877F2' }, { key: 'twitter', label: '🐦 Twitter', color: '#1DA1F2' }, { key: 'tiktok', label: '🎵 TikTok', color: '#ee1d52' }, { key: 'soundcloud', label: '☁️ SoundCloud', color: '#FF5500' }].map(({ key, label, color }) => {
                      const val = viewingArtist.socialMedia[key];
                      if (!val) return null;
                      let url = val;
                      if (!val.startsWith('http')) {
                        if (key === 'instagram') url = `https://instagram.com/${val.replace('@','')}`;
                        else if (key === 'twitter') url = `https://twitter.com/${val.replace('@','')}`;
                        else if (key === 'tiktok') url = `https://tiktok.com/@${val.replace('@','')}`;
                        else if (key === 'youtube') url = `https://youtube.com/${val}`;
                        else if (key === 'soundcloud') url = `https://soundcloud.com/${val}`;
                        else url = val;
                      }
                      return <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="social-btn text-xs" style={{ borderColor: color, color }}>{label}</a>;
                    })}
                  </div>
                </div>
              )}
              {viewingArtist.followerCounts && !viewingArtist.followerCountsBlocked && Object.values(viewingArtist.followerCounts).some(v => v) && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-electric text-xs font-bold uppercase tracking-wider">Follower Counts</p>
                    {viewingArtist.followerCountsUpdatedAt && (
                      <span className="text-xs text-gray-500">· Updated {(() => { const t = viewingArtist.followerCountsUpdatedAt?.seconds ? new Date(viewingArtist.followerCountsUpdatedAt.seconds * 1000) : new Date(viewingArtist.followerCountsUpdatedAt); return t.toLocaleDateString(); })()}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[{ key: 'instagram', icon: '📸', label: 'Instagram', color: '#E1306C' }, { key: 'youtube', icon: '▶️', label: 'YouTube', color: '#FF0000' }, { key: 'spotify', icon: '🎵', label: 'Spotify', color: '#1DB954' }, { key: 'facebook', icon: '👍', label: 'Facebook', color: '#1877F2' }, { key: 'tiktok', icon: '🎵', label: 'TikTok', color: '#ee1d52' }, { key: 'twitter', icon: '🐦', label: 'Twitter', color: '#1DA1F2' }, { key: 'soundcloud', icon: '☁️', label: 'SoundCloud', color: '#FF5500' }].map(({ key, icon, label, color }) => {
                      const val = viewingArtist.followerCounts[key];
                      if (!val) return null;
                      return (
                        <div key={key} className="flex items-center gap-2 p-3 rounded-lg" style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
                          <span className="text-xl">{icon}</span>
                          <div>
                            <p className="text-white font-bold text-base leading-tight">{Number(val).toLocaleString()}</p>
                            <p className="text-xs" style={{ color }}>{label}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-white/10">
              <button onClick={() => { setSelectedArtist(viewingArtist); setShowHireForm(true); setViewingArtist(null); setArtistStats(null); }} className="flex-1 btn btn-electric">
                <Send size={18} /><span>Hire This Artist</span>
              </button>
              <button onClick={() => { setViewingArtist(null); setArtistStats(null); }} className="btn btn-ghost">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Hire Request Modal ── */}
      {showHireForm && selectedArtist && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="gig-card w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">🎸 Hire {selectedArtist.artistName}</h2>
              <button onClick={() => { setShowHireForm(false); setSelectedArtist(null); }} className="text-white hover:text-neon text-2xl">✕</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white text-sm font-bold mb-1 block">Date *</label>
                  <input type="date" value={hireFormData.eventDate} onChange={e => setHireFormData({ ...hireFormData, eventDate: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:border-electric focus:outline-none text-sm" />
                </div>
                <div>
                  <label className="text-white text-sm font-bold mb-1 block">Time *</label>
                  <input type="time" value={hireFormData.eventTime} onChange={e => setHireFormData({ ...hireFormData, eventTime: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:border-electric focus:outline-none text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white text-sm font-bold mb-1 block">Duration (hrs)</label>
                  <input type="number" min="1" max="12" value={hireFormData.duration} onChange={e => setHireFormData({ ...hireFormData, duration: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:border-electric focus:outline-none text-sm" />
                </div>
                <div>
                  <label className="text-white text-sm font-bold mb-1 block">Budget (₹) *</label>
                  <input type="number" value={hireFormData.budget} onChange={e => setHireFormData({ ...hireFormData, budget: e.target.value })} placeholder="e.g. 5000" className="w-full px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:border-electric focus:outline-none text-sm" />
                </div>
              </div>
              <div>
                <label className="text-white text-sm font-bold mb-1 block">Message / Requirements</label>
                <textarea value={hireFormData.description} onChange={e => setHireFormData({ ...hireFormData, description: e.target.value })} placeholder="Tell the artist about your event..." rows={3} className="w-full px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:border-electric focus:outline-none text-sm resize-none" />
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-white/10">
              <button onClick={handleSendHireRequest} disabled={sendingRequest} className="flex-1 btn btn-electric">
                {sendingRequest ? '⏳ Sending...' : '📨 Send Hire Request'}
              </button>
              <button onClick={() => { setShowHireForm(false); setSelectedArtist(null); }} className="btn btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
